import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import HelpButton from '../../shared/HelpButton';
import { useRevenueReport } from '../hooks/useReportsApi';
import CashRegisterSection from './CashRegisterSection';

const COLORS = ['#3182ce', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function RevenueTab({ t, dateParams }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subTab, setSubTab] = useState(() => {
    return searchParams.get('subtab') === 'cashRegister' ? 1 : 0;
  });
  const { data, loading, error } = useRevenueReport(dateParams);

  // Sync subtab from URL changes
  useEffect(() => {
    const subtabParam = searchParams.get('subtab');
    const newSubTab = subtabParam === 'cashRegister' ? 1 : 0;
    if (newSubTab !== subTab) {
      setSubTab(newSubTab);
    }
  }, [searchParams]);

  const handleSubTabChange = (e, newValue) => {
    setSubTab(newValue);
    const newParams = { tab: 'revenue' };
    if (newValue === 1) {
      newParams.subtab = 'cashRegister';
    }
    setSearchParams(newParams, { replace: true });
  };

  if (error && subTab === 0) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      {/* Subtab toggle + Export */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              px: 2,
            },
          }}
        >
          <Tab label={t('reports.revenue.overview')} />
          <Tab label={t('reports.cashRegister.title')} />
        </Tabs>
        {subTab === 0 && (
          <ExportButtons reportType="revenue" params={dateParams} />
        )}
      </Box>

      {subTab === 0 ? (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={EuroIcon}
                label={t('reports.revenue.totalRevenue')}
                value={data?.total_revenue}
                loading={loading}
                format="currency"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={BuildIcon}
                label={t('reports.revenue.laborRevenue')}
                value={data?.labor_revenue}
                loading={loading}
                format="currency"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={InventoryIcon}
                label={t('reports.revenue.partsRevenue')}
                value={data?.parts_revenue}
                loading={loading}
                format="currency"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={TrendingUpIcon}
                label={t('reports.revenue.grossMargin')}
                value={data?.margin_percent}
                loading={loading}
                format="percent"
              />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Monthly Breakdown */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('reports.revenue.monthlyBreakdown')}
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data?.monthly_breakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period_label" />
                      <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="labor" name={t('reports.revenue.labor')} fill="#3182ce" stackId="a" />
                      <Bar dataKey="parts" name={t('reports.revenue.parts')} fill="#10b981" stackId="a" />
                      <Bar dataKey="other" name={t('reports.revenue.other')} fill="#f59e0b" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>

            {/* Labor vs Parts Pie */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('reports.revenue.breakdown')}
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" height={300} />
                ) : (() => {
                  const pieData = [
                    { name: t('reports.revenue.labor'), value: data?.labor_revenue || 0 },
                    { name: t('reports.revenue.parts'), value: data?.parts_revenue || 0 },
                    { name: t('reports.revenue.other'), value: data?.other_revenue || 0 },
                  ].filter(item => item.value > 0);
                  return pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="45%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend
                          verticalAlign="bottom"
                          formatter={(value, entry) => `${value} (${formatCurrency(entry.payload?.value ?? 0)})`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">{t('reports.noData')}</Typography>
                    </Box>
                  );
                })()}
              </Paper>
            </Grid>
          </Grid>

          {/* By Job Type Table */}
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.revenue.byJobType')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.revenue.jobType')}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {t('reports.revenue.count')}
                          <HelpButton text={t('reports.revenue.countHelp')} size={16} />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{t('reports.revenue.revenue')}</TableCell>
                      <TableCell align="right">{t('reports.revenue.average')}</TableCell>
                      <TableCell align="right">{t('reports.revenue.percent')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.by_job_type || []).map((job, index) => (
                      <TableRow key={index}>
                        <TableCell>{job.job_name}</TableCell>
                        <TableCell align="right">{job.count}</TableCell>
                        <TableCell align="right">{formatCurrency(job.revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(job.average)}</TableCell>
                        <TableCell align="right">{job.percent.toFixed(1)} %</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.by_job_type || data.by_job_type.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                          {t('reports.noData')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* By Part Table */}
          <Paper elevation={0} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.revenue.byPart')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.revenue.partName')}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {t('reports.revenue.count')}
                          <HelpButton text={t('reports.revenue.partCountHelp')} size={16} />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{t('reports.revenue.revenue')}</TableCell>
                      <TableCell align="right">{t('reports.revenue.cost')}</TableCell>
                      <TableCell align="right">{t('reports.revenue.margin')}</TableCell>
                      <TableCell align="right">{t('reports.revenue.marginPercent')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.by_part || []).map((part, index) => (
                      <TableRow key={index}>
                        <TableCell>{part.part_name}</TableCell>
                        <TableCell align="right">{part.count}</TableCell>
                        <TableCell align="right">{formatCurrency(part.revenue)}</TableCell>
                        <TableCell align="right">{part.cost > 0 ? formatCurrency(part.cost) : '-'}</TableCell>
                        <TableCell align="right">
                          <Typography
                            component="span"
                            sx={{ color: part.margin >= 0 ? 'success.main' : 'error.main', fontWeight: 500 }}
                          >
                            {part.cost > 0 ? formatCurrency(part.margin) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {part.margin_percent !== null ? `${part.margin_percent.toFixed(1)} %` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!data?.by_part || data.by_part.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          {t('reports.noData')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      ) : (
        <CashRegisterSection dateParams={dateParams} />
      )}
    </Box>
  );
}

export default withTranslation()(RevenueTab);
